<template>
  <div>
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
    />

    <!-- Search Section -->
    <div class="mt-5">
      <v-btn
        v-if="$IsUserInRole('AllgemeinerFeiertagskalenderAdministration')"
        class="float-right"
        color="success"
        flat
        title="Neuer Kalendereintrag erfassen"
        @click="createNew"
      >
        <i class="fas fa-plus mr-3" /> Erfassen
      </v-btn>

      <b-row>
        <b-col cols="4">
          <v-text-field
            id="searchInput"
            v-model="searchTerm"
            append-icon="fas fa-search"
            class="search--input"
            label="Nach Feiertagskalender suchen..."
          />
        </b-col>
        <b-col cols="1">
          <label class="mt-4 defaultLabel float-right"> Von </label>
        </b-col>
        <b-col cols="2">
          <u-date-picker
            v-model="searchFrom"
            label=""
            @change="getCalenders"
          />
        </b-col>
        <b-col cols="1">
          <label class="mt-4 defaultLabel float-right"> Bis </label>
        </b-col>
        <b-col cols="2">
          <u-date-picker
            v-model="searchTo"
            label=""
            @change="getCalenders"
          />
        </b-col>
        <b-col>
          <v-autocomplete
            v-model="selectedCityFilter"
            :items="cityFilterOptions"
            item-text="text"
            item-value="id"
            clearable
            label="Filter nach Ort"
          />
        </b-col>
      </b-row>
      <div
        class="mt-5 ml-3"
        style="width: 100%"
      >
        <b-select
          v-model="resultsPerPage"
          class="custom-select ml-1"
          style="width: 62px; height: 31px; font-size: 12px"
        >
          <option :value="10">
            10
          </option>
          <option :value="25">
            25
          </option>
          <option :value="50">
            50
          </option>
          <option :value="100">
            100
          </option>
        </b-select>
        <label
          style="font-weight: bold; margin-left: 3px"
        >Anzahl Resultate</label>
        <b-pagination
          v-model="currentPage"
          :per-page="resultsPerPage"
          :total-rows="totalNumberOfResults"
          class="float-right mr-4"
          first-text="<<"
          last-text=">>"
          next-text="Nächste"
          prev-text="Vorherige"
        />
      </div>
    </div>
    <!-- END Search Section -->

    <!-- Table Section -->
    <div class="ml-4 mr-4">
      <v-flex
        v-if="isLoading"
        justify-center
      >
        <u-loader />
      </v-flex>
      <table
        v-else
        class="w-100 calenderOverview ml-2 mt-4"
      >
        <thead>
          <tr>
            <th>Datum</th>
            <th>Bezeichnung</th>
            <th>Feiertagstyp</th>
            <th>Gültigkeitsbereich</th>
            <th>Geprüft</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="calendar in resourceCalendars"
            :key="calendar.id"
          >
            <td class="custom-td">
              {{ calendar.date | formatDate }}
              <br>
              {{ calendar.date | formatDay }}
            </td>
            <td class="custom-td">
              {{ calendar.holidayDescription }}
            </td>
            <td class="custom-td">
              {{
                publicHolidayTypes
                  | validateAndGetValue(calendar.publicHolidayType)
              }}
            </td>
            <td class="custom-td">
              <b-button
                v-b-toggle="calendar.id.toString()"
                class="m-1"
                @click="expandPanel(calendar.id)"
              >
                <i
                  v-if="calendar.isCollapsed"
                  class="fas fa-eye-slash mr-2"
                />
                <i
                  v-else
                  class="fas fa-eye mr-2"
                />
                {{ calendar.isCollapsed ? "Ausblenden" : "Einblenden" }}
              </b-button>
              <b-collapse
                :id="calendar.id.toString()"
                v-model="calendar.isCollapsed"
              >
                <b-card>
                  <v-flex
                    v-if="isLoadingTreeView[calendar.id]"
                    justify-center
                  >
                    <u-loader />
                  </v-flex>

                  <div
                    v-if="
                      calendar.isCollapsed && !isLoadingTreeView[calendar.id]
                    "
                  >
                    <div id="tree-overlay" />
                    <v-treeview
                      v-model="selectedLocations[calendar.id]"
                      :items="locationTree[calendar.id]"
                      :open-all="true"
                      activatable
                      class="mt-2"
                    >
                      <template v-slot:prepend="{ item }">
                        <i :class="getIcon(item)" />
                      </template>
                    </v-treeview>
                  </div>
                </b-card>
              </b-collapse>
            </td>
            <td class="custom-td">
              <v-checkbox
                v-model="calendar.validated"
                color="#e72b37"
                disabled
              />
            </td>
            <td class="custom-td">
              <v-btn
                v-if="$IsUserInRole('AllgemeinerFeiertagskalenderAdministration')"
                class="pt-0"
                color="#007bff"
                dark
                fab
                small
                title="Bearbeiten"
                @click="
                  resourceCalendarToEdit = calendar;
                  showModal('calendarEdit');
                "
              >
                <i class="fas fa-edit" />
              </v-btn>
              <v-btn
                v-if="$IsUserInRole('AllgemeinerFeiertagskalenderAdministration')"
                color="#e72b37"
                dark
                fab
                small
                title="Löschen"
                @click="
                  resourceCalendarToDeleteId = calendar.id;
                  showModal('confirmDeleteResourceCalendar');
                "
              >
                <i class="fas fa-trash" />
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- END Table Section -->

    <!-- --- Modal Section ---- -->
    <b-modal
      id="calendarCreate"
      ref="calendarCreate"
      hide-footer
      no-fade
      size="lg"
      title="Neuen Feiertagskalender erstellen"
    >
      <resource-calendar-create
        class="p-3"
        @canceled="hideModal('calendarCreate')"
        @saved="handleResourceCalendarSaved"
      />
    </b-modal>

    <b-modal
      id="calendarEdit"
      ref="calendarEdit"
      hide-footer
      no-fade
      size="lg"
      title="Bestehender Feiertagskalender bearbeiten"
    >
      <resource-calendar-edit
        :resource-calendar-to-edit="resourceCalendarToEdit"
        class="p-3"
        @canceled="handleResourceCalendarSaved"
        @saved="handleResourceCalendarSaved"
      />
    </b-modal>

    <b-modal
      id="confirmDeleteResourceCalendar"
      ref="confirmDeleteResourceCalendar"
      class="w-100"
      hide-footer
      no-fade
      scrollable
      title="Feiertagskalender löschen"
    >
      <div class="d-block mb-4">
        Sind Sie sicher, dass Sie Diesen Feiertagskalendereintrag löschen
        möchten?
      </div>
      <b-button
        variant="success"
        @click="deleteResourceCalendar()"
      >
        Ok
      </b-button>
      <b-button
        class="ml-2"
        variant="danger"
        @click="hideModal('confirmDeleteResourceCalendar')"
      >
        Abbrechen
      </b-button>
    </b-modal>
    <!-- ---End Modal Section--- -->
  </div>
</template>

<script>
import UDatePicker from "@/components/common/UDatePicker.vue";
import {
  DeleteRessourceCalendar,
  GetPublicholidaytype,
  GetCalenders,
  GetSelectedLocationTree,
} from "@/services/minimum.wage.services/resourceCalendar.minimum.wage";
import ResourceCalendarCreate from "@/components/resourceCalendar/tabs/ResourceCalendarCreate.vue";
import ResourceCalendarEdit from "@/components/resourceCalendar/tabs/ResourceCalendarEdit.vue";
import { ValidateTreeView } from "@/helpers/TreeViewHelperResourceCalendar";
import { GetAllCities } from "@/services/cantons.service.js";

export default {
  components: {
    UDatePicker,
    ResourceCalendarCreate,
    ResourceCalendarEdit,
  },
  filters: {
    formatDate: function (value) {
      if (!value) return "";
      const date = new Date(value);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day < 10 ? "0" : ""}${day}.${
        month < 10 ? "0" : ""
      }${month}.${year}`;
    },
    formatDay: function (value) {
      if (!value) return "";
      const wochentage = [
        "Sonntag",
        "Montag",
        "Dienstag",
        "Mittwoch",
        "Donnerstag",
        "Freitag",
        "Samstag",
      ];
      const date = new Date(value);
      const wochentagIndex = date.getDay();
      const wochentag = wochentage[wochentagIndex];
      return wochentag;
    },
    validateAndGetValue: function (inputObj, key) {
      if (
        !inputObj ||
        typeof inputObj !== "object" ||
        typeof key === "undefined"
      ) {
        return "";
      }

      if (key in inputObj) {
        return inputObj[key];
      }

      return "";
    },
  },
  props: {
    refreshToken: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isLoading: false,
      errorObject: null,
      searchTerm: "",
      searchFrom: `${new Date().getFullYear()}-01-01`,
      searchTo: "",
      resourceCalendars: [],
      cityFilterOptions: [],
      selectedCityFilter: 0,
      locationTree: {},
      totalNumberOfResults: 0,
      currentPage: 1,
      resultsPerPage: 25,
      sortOrder: 0,
      sortedProperty: "",
      selectedLocations: {},
      resourceCalendarToDeleteId: 0,
      resourceCalendarToEdit: null,
      publicHolidayTypes: "",
      expandedPanels: [],
      isLoadingTreeView: {},
    };
  },
  watch: {
    searchTerm() {
      this.getCalenders();
    },
    currentPage() {
      this.getCalenders();
    },
    resultsPerPage() {
      this.currentPage = 1;
      this.getCalenders();
    },
    selectedCityFilter() {
      this.getCalenders();
    },
    refreshToken() {
      this.reloadCalendarData();
    },
  },
  created() {
    this.isLoading = true;
    this.loadPublicholidayTypes();
  },
  methods: {
    getIcon(node) {
      if (node.selected) {
        return "fas fa-check-square checkbox-right";
      }

      return "fas fa-minus-square checkbox-minus";
    },
    getCalenders() {
      this.isLoading = true;
      GetCalenders(
        this.resultsPerPage,
        this.currentPage,
        this.searchTerm,
        this.sortedProperty,
        this.sortOrder,
        this.searchFrom,
        this.searchTo,
        this.selectedCityFilter,
        (response) => {
          this.resourceCalendars = response.ressourceCalendars;
          this.totalNumberOfResults = response.numberOfItems;
          this.isLoading = false;
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },
    expandPanel(calenderId) {
      if (!this.expandedPanels.some((id) => id === calenderId)) {
        this.$nextTick(() => {
          this.$set(this.selectedLocations, calenderId, []);
          this.$set(this.locationTree, calenderId, []);
          this.$set(this.isLoadingTreeView, calenderId, true);
          this.loadLocationTreeOfCalendar(calenderId);
        });

        this.expandedPanels.push(calenderId);
      }
    },
    loadPublicholidayTypes() {
      GetPublicholidaytype(
        (response) => {
          this.publicHolidayTypes = response;
          this.loadCities();
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },
    loadCities() {
      GetAllCities(
        (response) => {
          this.cityFilterOptions = response.map((x) => {
            return {
              id: x.cityId,
              text: `${x.plz} ${x.city} (${x.district} ${x.cantonCode})`,
            };
          });
          this.getCalenders();
        },
        (error) => {
          this.PlzErrorObject = error;
        },
      );
    },
    loadLocationTreeOfCalendar(calendarId) {
      GetSelectedLocationTree(
        calendarId,
        (response) => {
          const validatedTreeView = ValidateTreeView(response);
          this.locationTree[calendarId] = validatedTreeView.treeItems;
          this.selectedLocations[calendarId] = validatedTreeView.selectedItems;

          this.$set(this.isLoadingTreeView, calendarId, false);
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    reloadCalendarData() {
      this.locationTree = {};
      this.selectedLocations = {};
      this.isLoadingTreeView = {};
      this.expandedPanels = [];
      this.resourceCalendars = [];
      this.getCalenders();
    },
    handleResourceCalendarSaved() {
      this.reloadCalendarData();
      this.hideModal("calendarCreate");
      this.hideModal("calendarEdit");
    },
    createNew() {
      this.showModal("calendarCreate");
    },
    deleteResourceCalendar() {
      DeleteRessourceCalendar(
        this.resourceCalendarToDeleteId,
        () => {
          this.hideModal("confirmDeleteResourceCalendar");
          this.handleResourceCalendarSaved();
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    hideModal(modalId) {
      this.$refs[modalId].hide();
    },
    showModal(modalId) {
      this.$refs[modalId].show();
    },
  },
};
</script>

<style scoped>
.custom-td {
  vertical-align: top;
}

.calenderOverview {
  font-size: small;
  table-layout: fixed;
}

.calenderOverview thead th:nth-child(1) {
  width: 7%;
}

.calenderOverview thead th:nth-child(2) {
  width: 20%;
}

.calenderOverview thead th:nth-child(3) {
  width: 10%;
}

.calenderOverview thead th:nth-child(4) {
  width: 30%;
}

.calenderOverview thead th:nth-child(5) {
  width: 10%;
}

.calenderOverview tbody tr {
  border-bottom: 1px solid gainsboro;
}

.calenderOverview td,
.calenderOverview th {
  padding: 8px 4px;
}

/deep/ .page-item.active .page-link {
  z-index: 1;
  color: #fff;
  background-color: #e72b37;
  border-color: red;
}

/deep/ .page-link:hover {
  z-index: 2;
  color: red;
  text-decoration: none;
  background-color: #fdd;
  border-color: #fdd;
}

/deep/ .v-input--selection-controls__input {
  height: 0px;
}

/deep/ .v-treeview-node__root {
  cursor: auto;
}

/deep/ .v-treeview-node__content {
  pointer-events: none;
}

/deep/ .v-treeview-node__toggle {
  transform: rotate(180deg);
}

.checkbox-right {
  color: #e72b37;
  font-size: 1.73em !important;
}

.checkbox-minus {
  color: rgba(0, 0, 0, 0.54);
  font-size: 1.73em;
}

#tree-overlay {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  opacity: 0.5;
  background-color: white;
}
</style>
